'use client';

import { c as _c } from "react/compiler-runtime";
import { createContext, PropsWithChildren, useContext, useState } from 'react';
import type { ResponsiveSheetPresentation } from '@repo/ui/components';
import { LocationPlaceValue } from '@repo/features/location';
import type { CompanyLocationFormValues } from '@repo/form/location';
import { CreateLocationSheet } from '@/app/@protected/_components/create-location/sheet.tsx';
export type OpenCreateLocationSheetOptions = {
  companyId: number;
  initialSearchQuery?: string;
  initialValues?: Partial<CompanyLocationFormValues>;
  mode: 'create';
  onCreated?: (place: LocationPlaceValue) => void;
  orgIdOverride?: number;
  presentation?: ResponsiveSheetPresentation;
};
export type OpenEditLocationSheetOptions = {
  companyId: number;
  locationId: number;
  mode: 'edit';
  onUpdated?: (place: LocationPlaceValue) => void;
  orgIdOverride?: number;
  presentation?: ResponsiveSheetPresentation;
};
export type OpenLocationSheetOptions = OpenCreateLocationSheetOptions | OpenEditLocationSheetOptions;
type Context = {
  closeCreateLocationSheet: () => void;
  formKey: number;
  isOpen: boolean;
  openCreateLocationSheet: (options: OpenCreateLocationSheetOptions) => void;
  openEditLocationSheet: (options: OpenEditLocationSheetOptions) => void;
  options: null | OpenLocationSheetOptions;
};
const CreateLocationSheetContext = createContext<Context | null>(null);
export const CreateLocationSheetProvider = t0 => {
  const $ = _c(11);
  const {
    children
  } = t0;
  const [formKey, setFormKey] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState(null);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setIsOpen(false);
      setOptions(null);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const closeCreateLocationSheet = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = nextOptions => {
      setOptions(nextOptions);
      setFormKey(_temp);
      setIsOpen(true);
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const openCreateLocationSheet = t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = nextOptions_0 => {
      setOptions(nextOptions_0);
      setFormKey(_temp2);
      setIsOpen(true);
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const openEditLocationSheet = t3;
  let t4;
  if ($[3] !== formKey || $[4] !== isOpen || $[5] !== options) {
    t4 = {
      closeCreateLocationSheet,
      formKey,
      isOpen,
      openCreateLocationSheet,
      openEditLocationSheet,
      options
    };
    $[3] = formKey;
    $[4] = isOpen;
    $[5] = options;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <CreateLocationSheet data-sentry-element="CreateLocationSheet" data-sentry-source-file="context.tsx" />;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== children || $[9] !== t4) {
    t6 = <CreateLocationSheetContext.Provider value={t4} data-sentry-element="CreateLocationSheetContext.Provider" data-sentry-component="CreateLocationSheetProvider" data-sentry-source-file="context.tsx">{children}{t5}</CreateLocationSheetContext.Provider>;
    $[8] = children;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
};
export const useCreateLocationSheet = () => {
  const context = useContext(CreateLocationSheetContext);
  if (!context) {
    throw new Error("useCreateLocationSheet must be used within a CreateLocationSheetProvider");
  }
  return context;
};
function _temp(currentValue) {
  return currentValue + 1;
}
function _temp2(currentValue_0) {
  return currentValue_0 + 1;
}